import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('09:00');

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const [apptRes, blockedRes, scheduleRes] = await Promise.all([
        api.get('/admin/appointments', {
          params: {
            startDate: startOfMonth.toISOString().split('T')[0],
            endDate: endOfMonth.toISOString().split('T')[0]
          }
        }),
        api.get('/admin/availability/block', {
          params: {
            startDate: startOfMonth.toISOString().split('T')[0],
            endDate: endOfMonth.toISOString().split('T')[0]
          }
        }),
        api.get('/admin/availability/weekly')
      ]);

      console.log('Appointments response:', apptRes.data);
      console.log('Blocked slots response:', blockedRes.data);
      console.log('Weekly schedule response:', scheduleRes.data);

      setAppointments(apptRes.data.appointments || []);
      setBlockedSlots(blockedRes.data.blockedSlots || []);
      setWeeklySchedule(scheduleRes.data.schedule || []);
    } catch (error) {
      console.error('Fetch calendar data error:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Add empty slots for days before month starts
    const startDay = firstDay.getDay();
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Add all days in month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }

    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return { appointments: [], blocked: [] };

    const dateStr = date.toISOString().split('T')[0];
    const dayOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][date.getDay()];

    // Compare dates by extracting just the date part (ignoring time)
    const appts = appointments.filter(a => {
      const apptDate = a.appointmentDate.split('T')[0];
      return apptDate === dateStr;
    });

    const blocked = blockedSlots.filter(b => {
      const blockDate = b.date.split('T')[0];
      return blockDate === dateStr;
    });

    const schedule = weeklySchedule.find(s => s.dayOfWeek === dayOfWeek);

    return { appointments: appts, blocked, isAvailable: schedule?.isActive };
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const days = getDaysInMonth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-primary">Calendar</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowBookModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            + Book Appointment
          </button>
          <button
            onClick={() => setShowBlockModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            + Block Time
          </button>
        </div>
      </div>

      {/* Calendar Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={handlePrevMonth}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ← Previous
          </button>
          <h2 className="text-2xl font-bold text-gray-800">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button
            onClick={handleNextMonth}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Next →
          </button>
        </div>

        {/* Day Labels */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const { appointments: dayAppts, blocked, isAvailable } = getEventsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const isPast = date < new Date().setHours(0, 0, 0, 0);

            return (
              <div
                key={date.toISOString()}
                onClick={() => handleDateClick(date)}
                className={`
                  aspect-square border rounded-lg p-2 cursor-pointer transition-all
                  ${isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                  ${isPast ? 'bg-gray-50' : 'hover:bg-gray-50'}
                  ${!isAvailable && !isPast ? 'bg-red-50' : ''}
                  ${selectedDate?.toDateString() === date.toDateString() ? 'ring-2 ring-blue-400' : ''}
                `}
              >
                <div className="text-sm font-semibold text-gray-700 mb-1">
                  {date.getDate()}
                </div>
                <div className="space-y-1 text-xs">
                  {dayAppts.slice(0, 2).map(appt => (
                    <div
                      key={appt.id}
                      className="bg-green-100 text-green-800 px-1 py-0.5 rounded truncate"
                      title={`${appt.startTime} - ${appt.user?.firstName} ${appt.user?.lastName}`}
                    >
                      {appt.startTime}
                    </div>
                  ))}
                  {blocked.slice(0, 2).map(block => (
                    <div
                      key={block.id}
                      className="bg-red-100 text-red-800 px-1 py-0.5 rounded truncate"
                      title={block.reason || 'Blocked'}
                    >
                      {block.startTime && block.endTime
                        ? `${block.startTime.substring(0, 5)}-${block.endTime.substring(0, 5)}`
                        : 'Full Day'}
                    </div>
                  ))}
                  {dayAppts.length > 2 && (
                    <div className="text-gray-500">+{dayAppts.length - 2} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Date Details */}
        {selectedDate && (
          <div className="mt-6 border-t pt-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </h3>

            {(() => {
              const { appointments: dayAppts, blocked } = getEventsForDate(selectedDate);

              return (
                <div className="space-y-4">
                  {dayAppts.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Appointments</h4>
                      <div className="space-y-2">
                        {dayAppts.map(appt => (
                          <div key={appt.id} className="bg-green-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-medium">{appt.startTime} - {appt.endTime}</div>
                                <div className="text-sm text-gray-600">
                                  {appt.user?.firstName} {appt.user?.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{appt.user?.email}</div>
                              </div>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                appt.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                appt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {appt.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {blocked.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Blocked Times</h4>
                      <div className="space-y-2">
                        {blocked.map(slot => (
                          <div key={slot.id} className="bg-red-50 p-3 rounded-lg flex justify-between items-center">
                            <div>
                              <div className="font-medium">
                                {slot.startTime && slot.endTime
                                  ? `${slot.startTime} - ${slot.endTime}`
                                  : 'All Day'}
                              </div>
                              {slot.reason && (
                                <div className="text-sm text-gray-600">{slot.reason}</div>
                              )}
                            </div>
                            <button
                              onClick={async () => {
                                if (window.confirm('Remove this block?')) {
                                  try {
                                    await api.delete(`/admin/availability/block/${slot.id}`);
                                    fetchData();
                                  } catch (error) {
                                    alert('Failed to remove block');
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {dayAppts.length === 0 && blocked.length === 0 && (
                    <div className="text-center text-gray-500 py-4">
                      No appointments or blocks for this date
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Block Time Modal */}
      {showBlockModal && (
        <BlockTimeModal
          selectedDate={selectedDate}
          onClose={() => setShowBlockModal(false)}
          onSuccess={() => {
            setShowBlockModal(false);
            fetchData();
          }}
        />
      )}

      {/* Book Appointment Modal */}
      {showBookModal && (
        <BookAppointmentModal
          selectedDate={selectedDate}
          onClose={() => setShowBookModal(false)}
          onSuccess={() => {
            setShowBookModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Block Time Modal Component
const BlockTimeModal = ({ selectedDate, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date: selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    reason: '',
    allDay: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.post('/admin/availability/block', {
        date: formData.date,
        startTime: formData.allDay ? null : formData.startTime,
        endTime: formData.allDay ? null : formData.endTime,
        reason: formData.reason
      });

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to block time');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">Block Time</h2>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.allDay}
                onChange={(e) => setFormData({ ...formData, allDay: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Block entire day</span>
            </label>
          </div>

          {!formData.allDay && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  required={!formData.allDay}
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  required={!formData.allDay}
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (optional)
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Meeting, break, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Blocking...' : 'Block Time'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Book Appointment Modal Component
const BookAppointmentModal = ({ selectedDate, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    appointmentDate: selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    startTime: '09:00',
    serviceName: '',
    durationMinutes: 60,
    price: 0,
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showOverride, setShowOverride] = useState(false);
  const [blockedSlotId, setBlockedSlotId] = useState(null);

  const handleSubmit = async (e, override = false) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // If overriding, first delete the blocked slot
      if (override && blockedSlotId) {
        await api.delete(`/admin/availability/block/${blockedSlotId}`);
      }

      await api.post('/admin/appointments/create', formData);
      onSuccess();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create appointment';

      // If it's a "time slot is blocked" error, show override option
      if (errorMsg.includes('blocked')) {
        setError(errorMsg);
        setShowOverride(true);

        // Try to find which block is causing the conflict
        try {
          const blocksRes = await api.get('/admin/availability/block', {
            params: {
              startDate: formData.appointmentDate,
              endDate: formData.appointmentDate
            }
          });

          if (blocksRes.data.blockedSlots?.length > 0) {
            setBlockedSlotId(blocksRes.data.blockedSlots[0].id);
          }
        } catch (e) {
          console.error('Failed to fetch blocks', e);
        }
      } else {
        setError(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
        <h2 className="text-2xl font-bold mb-4">Book Appointment</h2>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Optional for admin bookings"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.appointmentDate}
                onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time *
              </label>
              <input
                type="time"
                required
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Name
            </label>
            <input
              type="text"
              value={formData.serviceName}
              onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
              placeholder="e.g., Upper Lip Electrolysis"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes) *
              </label>
              <input
                type="number"
                required
                min="15"
                step="15"
                value={formData.durationMinutes}
                onChange={(e) => setFormData({ ...formData, durationMinutes: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price ($) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {showOverride && (
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={saving}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Overriding...' : 'Override Block & Book'}
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Booking...' : 'Book Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Calendar;
